import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Download as DownloadIcon } from "@mui/icons-material";
import TableWithTopScroll from "components/common/TableWithTopScroll";
import XLSX from "xlsx-js-style";
import { saveAs } from "file-saver";
import { motion } from "framer-motion";
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Button,
  Chip,
  CircularProgress,
  Typography,
  IconButton,
  InputAdornment,
  Stack,
  useTheme,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Divider,
  Grid,
  MenuItem,
  Tooltip,
} from "@mui/material";
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Event as EventIcon,
  AttachMoney as MoneyIcon,
  Business as BusinessIcon,
  Close as CloseIcon,
  Visibility as ViewIcon,
  LocalOffer as AllowanceIcon,
  LocalGasStation as FuelIcon,
  Phone as PhoneIcon,
  DriveEta as CarIcon,
  Build as MaintenanceIcon,
  FlightTakeoff as LtaIcon,
  Percent as PercentIcon,
  AccountBalance as TaxIcon,
  Work as BonusIcon,
  AccessTime as OvertimeIcon,
  Receipt as PTIcon,
  Person as PersonIcon,
  DateRange as DateIcon,
  Calculate as CalculateIcon,
  Home as HomeIcon,
} from "@mui/icons-material";
import {
  fetchSalaryStructures,
  deleteExistingSalaryStructure,
} from "store/features/company/payrollManagement/salaryStructure/salaryStructureActions";
import {
  selectSalaryStructures,
  selectSalaryStructureLoading,
  selectSalaryStructurePagination,
} from "store/features/company/payrollManagement/salaryStructure/salaryStructureSelectors";
import AddSalaryStructureModal from "./AddSalaryStructureModal";
import EditSalaryStructureModal from "./EditSalaryStructureModal";
import salaryStructureSvg from "assets/folders/salary-structure.svg";
import viewDetailsSvg from "assets/modals/details.svg";

import usePermissions from "hooks/usePermissions";

const SalaryStructure = () => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const salaryStructures = useSelector(selectSalaryStructures);
  const loading = useSelector(selectSalaryStructureLoading);

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [selectedSalaryStructure, setSelectedSalaryStructure] = useState(null);
  const [viewingStructure, setViewingStructure] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [salaryStructureToDelete, setSalaryStructureToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const pagination = useSelector(selectSalaryStructurePagination);

  const { hasPermission, hasAnyPermission } = usePermissions();
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);

  useEffect(() => {
    dispatch(fetchSalaryStructures({ page, limit }));
  }, [dispatch, page, limit]);

  const filteredSalaryStructures = salaryStructures.filter((structure) => {
    const matchesSearch =
      searchTerm === "" ||
      structure.first_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      structure.last_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      structure.employee_code
        ?.toLowerCase()
        .includes(searchTerm.toLowerCase()) ||
      structure.email?.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesStatus =
      statusFilter === "all" || structure.status === statusFilter;

    return matchesSearch && matchesStatus;
  });

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    try {
      if (dateString.includes("T")) {
        const [datePart] = dateString.split("T");
        const [year, month, day] = datePart.split("-");
        const monthNames = [
          "Jan",
          "Feb",
          "Mar",
          "Apr",
          "May",
          "Jun",
          "Jul",
          "Aug",
          "Sep",
          "Oct",
          "Nov",
          "Dec",
        ];
        return `${parseInt(day)} ${monthNames[parseInt(month) - 1]} ${year}`;
      }
      if (dateString.match(/^\d{4}-\d{2}-\d{2}$/)) {
        const [year, month, day] = dateString.split("-");
        const monthNames = [
          "Jan",
          "Feb",
          "Mar",
          "Apr",
          "May",
          "Jun",
          "Jul",
          "Aug",
          "Sep",
          "Oct",
          "Nov",
          "Dec",
        ];
        return `${parseInt(day)} ${monthNames[parseInt(month) - 1]} ${year}`;
      }
      return dateString;
    } catch (e) {
      return "-";
    }
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return "-";
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch (e) {
      return "-";
    }
  };

  const formatCurrency = (amount) => {
    if (!amount) return "₹0";
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(parseFloat(amount));
  };

  const StatusBadge = ({ status }) => {
    if (status === "active") {
      return (
        <Chip
          icon={<CheckCircleIcon sx={{ fontSize: 14 }} />}
          label="Active"
          size="small"
          sx={{
            backgroundColor: "#d1fae5",
            color: "#065f46",
            fontWeight: 500,
            fontSize: "0.75rem",
            height: "24px",
            ...(theme.palette.mode === "dark" && {
              backgroundColor: "rgba(16,185,129,0.25)",
              color: "#6ee7b7",
            }),
          }}
        />
      );
    }
    return (
      <Chip
        icon={<CancelIcon sx={{ fontSize: 14 }} />}
        label="Inactive"
        size="small"
        sx={{
          backgroundColor: "#fee2e2",
          color: "#991b1b",
          fontWeight: 500,
          fontSize: "0.75rem",
          height: "24px",
          ...(theme.palette.mode === "dark" && {
            backgroundColor: "rgba(239,68,68,0.25)",
            color: "#fca5a5",
          }),
        }}
      />
    );
  };

  const handleDeleteClick = (structure) => {
    setSalaryStructureToDelete(structure);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (salaryStructureToDelete) {
      setDeleting(true);
      const result = await dispatch(
        deleteExistingSalaryStructure(salaryStructureToDelete.id),
      );
      if (result.success) {
        dispatch(fetchSalaryStructures());
        setDeleteDialogOpen(false);
        setSalaryStructureToDelete(null);
      }
      setDeleting(false);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setSalaryStructureToDelete(null);
  };

  const handleViewClick = (structure) => {
    setViewingStructure(structure);
    setShowViewModal(true);
  };

  if (!hasPermission("salary_structure.view")) {
    return null;
  }
  const exportSalaryStructuresToExcel = () => {
    if (!filteredSalaryStructures || filteredSalaryStructures.length === 0) {
      alert("No data to export");
      return;
    }

    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.aoa_to_sheet([]);

    // Yellow header style
    const yellowHeaderStyle = {
      font: {
        bold: true,
        sz: 12,
        color: { rgb: "000000" },
      },
      fill: {
        fgColor: { rgb: "FFFF00" },
      },
      alignment: {
        horizontal: "center",
        vertical: "center",
      },
      border: {
        top: { style: "thin", color: { rgb: "000000" } },
        bottom: { style: "thin", color: { rgb: "000000" } },
        left: { style: "thin", color: { rgb: "000000" } },
        right: { style: "thin", color: { rgb: "000000" } },
      },
    };

    let row = 0;

    // Title
    XLSX.utils.sheet_add_aoa(ws, [["REMUNERATION STRUCTURES REPORT"]], {
      origin: `A${row + 1}`,
    });
    ws["!merges"] = [{ s: { r: row, c: 0 }, e: { r: row, c: 10 } }];
    ws[`A${row + 1}`].s = {
      font: { bold: true, sz: 14, color: { rgb: "000000" } },
      fill: { fgColor: { rgb: "FFFF00" } },
      alignment: { horizontal: "center", vertical: "center" },
    };
    row += 2;

    // Summary
    XLSX.utils.sheet_add_aoa(ws, [["SUMMARY"]], { origin: `A${row + 1}` });
    ws["!merges"].push({ s: { r: row, c: 0 }, e: { r: row, c: 1 } });
    ws[`A${row + 1}`].s = yellowHeaderStyle;
    row++;

    XLSX.utils.sheet_add_aoa(
      ws,
      [
        ["Metric", "Value"],
        ["Total Structures", filteredSalaryStructures.length],
        [
          "Active",
          filteredSalaryStructures.filter((s) => s.status === "active").length,
        ],
        [
          "Inactive",
          filteredSalaryStructures.filter((s) => s.status === "inactive")
            .length,
        ],
      ],
      { origin: `A${row + 1}` },
    );
    row += 6;

    // Headers
    const headers = [
      "ID",
      "Employee ID",
      "Employee Code",
      "Employee Name",
      "Email",
      "Phone",
      "Basic Remuneration",
      "Gross Remuneration",
      "Total Deduction",
      "Net Remuneration",
      "Status",
      "Created At",
    ];
    XLSX.utils.sheet_add_aoa(ws, [headers], { origin: `A${row + 1}` });

    // Apply yellow header style to header row
    for (let i = 0; i < headers.length; i++) {
      const cellRef = XLSX.utils.encode_cell({ r: row, c: i });
      if (ws[cellRef]) {
        ws[cellRef].s = yellowHeaderStyle;
      }
    }
    row++;

    // Data
    filteredSalaryStructures.forEach((structure) => {
      XLSX.utils.sheet_add_aoa(
        ws,
        [
          [
            structure.id || "",
            structure.employee_id || "",
            structure.employee_code || "",
            `${structure.first_name || ""} ${structure.last_name || ""}`.trim(),
            structure.email || "",
            structure.phone || "",
            structure.basic ? formatCurrency(structure.basic) : "",
            structure.gross_salary
              ? formatCurrency(structure.gross_salary)
              : "",
            structure.total_deduction
              ? formatCurrency(structure.total_deduction)
              : "",
            structure.net_salary ? formatCurrency(structure.net_salary) : "",
            structure.status || "",
            structure.created_at ? formatDateTime(structure.created_at) : "",
          ],
        ],
        { origin: `A${row + 1}` },
      );
      row++;
    });

    // Column widths
    ws["!cols"] = headers.map((_, index) => {
      const widths = {
        0: 15, // ID
        1: 18, // Employee ID
        2: 20, // Employee Code
        3: 28, // Employee Name
        4: 30, // Email
        5: 18, // Phone
        6: 20, // Basic Salary
        7: 20, // Gross Salary
        8: 22, // Total Deduction
        9: 20, // Net Salary
        10: 18, // Status
        11: 25, // Created At
      };
      return { wch: widths[index] || 20 };
    });

    XLSX.utils.book_append_sheet(wb, ws, "Salary Structures");

    // Write file
    const wbout = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    const blob = new Blob([wbout], { type: "application/octet-stream" });
    saveAs(
      blob,
      `Salary_Structures_Report_${new Date().toISOString().split("T")[0]}.xlsx`,
    );
  };
  return (
    <Box
      sx={{
        minHeight: "100vh",
        // background:
        //   theme.palette.mode === "dark"
        //     ? "linear-gradient(135deg, #0a0f1e 0%, #0f172a 50%, #1e1b2e 100%)"
        //     : "linear-gradient(135deg, #fffbeb 0%, #ffffff 50%, #fefce8 100%)",
        p: 3,
      }}
    >
      {/* Header Section */}
      <Box sx={{ mb: 4 }}>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={2}
          alignItems={{ xs: "flex-start", sm: "center" }}
          justifyContent="space-between"
          sx={{ width: "100%" }}
        >
          <Stack
            direction="row"
            spacing={2}
            alignItems="center"
            sx={{ flex: 1 }}
          >
            <Box
              component="img"
              src={salaryStructureSvg}
              alt="Salary Structures"
              sx={{
                width: { xs: 64, lg: 80 },
                height: { xs: 64, lg: 80 },
                objectFit: "contain",
              }}
            />
            <Box>
              <Typography
                variant="h4"
                component="h1"
                sx={{
                  fontWeight: "bold",
                  color: "text.primary",
                }}
              >
                Remuneration Structures
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: "text.secondary",
                  mt: 0.5,
                }}
              >
                Manage employee salary structures and compensation
              </Typography>
            </Box>
          </Stack>

          <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
            {/* Export Excel Button */}
            <Button
              variant="contained"
              startIcon={<DownloadIcon />}
              onClick={exportSalaryStructuresToExcel}
              sx={{
                background: "linear-gradient(135deg, #25a55f 0%, #1e8b4e 100%)",
                color: "#ffffff",
                borderRadius: "8px",
                px: 2.5,
                py: 0.5,
                fontSize: "0.8125rem",
                fontWeight: 500,
                textTransform: "none",
                minWidth: "auto",
                whiteSpace: "nowrap",
                boxShadow: "none",
                transition: "all 0.2s ease",
                height: "36px",
                "&:hover": {
                  background:
                    "linear-gradient(135deg, #1e8b4e 0%, #16723e 100%)",
                  boxShadow: "0 2px 8px rgba(37, 165, 95, 0.3)",
                  transform: "translateY(-1px)",
                },
                "&:active": {
                  transform: "translateY(0)",
                },
              }}
            >
              Export Excel
            </Button>

            {hasPermission("salary_structure.create") && (
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => setShowCreateModal(true)}
                sx={{
                  background:
                    "linear-gradient(135deg, #f59e0b 0%, #d97706 100%)",
                  borderRadius: "10px",
                  px: 2.5,
                  py: 1,
                  fontSize: "0.875rem",
                  fontWeight: 500,
                  textTransform: "none",
                  minWidth: "auto",
                  whiteSpace: "nowrap",
                  boxShadow: "none",
                  transition: "all 0.2s ease",
                  "&:hover": {
                    background:
                      "linear-gradient(135deg, #d97706 0%, #b45309 100%)",
                    boxShadow: "0 2px 8px rgba(245, 158, 11, 0.3)",
                    transform: "translateY(-1px)",
                  },
                  "&:active": {
                    transform: "translateY(0)",
                  },
                }}
              >
                Add Remuneration Structure
              </Button>
            )}
          </Box>
        </Stack>
      </Box>

      {/* Search and Filter Section */}
      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={2}
        alignItems={{ sm: "center" }}
        justifyContent="space-between"
        sx={{ mb: 3 }}
      >
        <TextField
          placeholder="Search by employee name, code or email..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          variant="outlined"
          size="small"
          sx={{
            flex: 1,
            maxWidth: { sm: 400 },
            "& .MuiOutlinedInput-root": {
              borderRadius: "12px",
              bgcolor: "background.paper",
              "& fieldset": {
                borderColor:
                  theme.palette.mode === "dark" ? "#334155" : "#e5e7eb",
              },
              "&:hover fieldset": {
                borderColor: "#f59e0b",
              },
              "&.Mui-focused fieldset": {
                borderColor: "#f59e0b",
                borderWidth: "2px",
              },
            },
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: "#9ca3af" }} />
              </InputAdornment>
            ),
          }}
        />
        <Stack direction="row" spacing={1}>
          {["all", "active", "inactive"].map((filter) => (
            <Button
              key={filter}
              variant={statusFilter === filter ? "contained" : "outlined"}
              onClick={() => setStatusFilter(filter)}
              sx={{
                textTransform: "capitalize",
                borderRadius: "8px",
                px: 2,
                py: 0.7,
                fontSize: "0.875rem",
                fontWeight: 500,
                ...(statusFilter === filter && {
                  background:
                    "linear-gradient(135deg, #f59e0b 0%, #d97706 100%)",
                  boxShadow: "0 4px 6px -1px rgba(245, 158, 11, 0.25)",
                  "&:hover": {
                    background:
                      "linear-gradient(135deg, #d97706 0%, #b45309 100%)",
                  },
                }),
                ...(statusFilter !== filter && {
                  borderColor:
                    theme.palette.mode === "dark" ? "#475569" : "#d1d5db",
                  color: theme.palette.mode === "dark" ? "#d1d5db" : "#6b7280",
                  "&:hover": {
                    borderColor: "#f59e0b",
                    backgroundColor:
                      theme.palette.mode === "dark"
                        ? "rgba(245,158,11,0.1)"
                        : "#fffbeb",
                  },
                }),
              }}
            >
              {filter.charAt(0).toUpperCase() + filter.slice(1)}
            </Button>
          ))}
        </Stack>
      </Stack>

      {/* Loading State */}
      {loading ? (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: 400,
          }}
        >
          <Stack alignItems="center" spacing={2}>
            <CircularProgress size={48} sx={{ color: "primary.main" }} />
            <Typography
              sx={{
                color: "text.secondary",
              }}
            >
              Loading salary structures...
            </Typography>
          </Stack>
        </Box>
      ) : (
        <Paper
          elevation={0}
          sx={{
            borderRadius: "16px",
            overflow: "auto",
            boxShadow:
              theme.palette.mode === "dark"
                ? "0 10px 25px -5px rgba(0,0,0,0.3)"
                : "0 10px 25px -5px rgba(0,0,0,0.05), 0 8px 10px -6px rgba(0,0,0,0.02)",
            border: `1px solid ${
              theme.palette.mode === "dark" ? "#1e293b" : "#f3f4f6"
            }`,
          }}
        >
          <TableWithTopScroll deps={[filteredSalaryStructures.length, loading]}>
            <Table sx={{ minWidth: 1200 }}>
              <TableHead
                sx={{
                  backgroundColor:
                    theme.palette.mode === "dark" ? "#1e293b" : "#f9fafb",
                }}
              >
                <TableRow>
                  <TableCell
                    sx={{
                      fontWeight: 700,
                      fontSize: "0.75rem",
                      textTransform: "uppercase",
                    }}
                  >
                    ID / Employee
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: 700,
                      fontSize: "0.75rem",
                      textTransform: "uppercase",
                    }}
                  >
                    Employee Details
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: 700,
                      fontSize: "0.75rem",
                      textTransform: "uppercase",
                    }}
                  >
                    Basic Remuneration
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: 700,
                      fontSize: "0.75rem",
                      textTransform: "uppercase",
                    }}
                  >
                    Gross Remuneration
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: 700,
                      fontSize: "0.75rem",
                      textTransform: "uppercase",
                    }}
                  >
                    Total Deduction
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: 700,
                      fontSize: "0.75rem",
                      textTransform: "uppercase",
                    }}
                  >
                    Net Remuneration
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: 700,
                      fontSize: "0.75rem",
                      textTransform: "uppercase",
                    }}
                  >
                    Status
                  </TableCell>
                  {hasAnyPermission([
                    "salary_structure.view",
                    "salary_structure.update",
                    "salary_structure.delete",
                  ]) && (
                    <TableCell
                      align="center"
                      sx={{
                        fontWeight: 700,
                        fontSize: "0.75rem",
                        textTransform: "uppercase",
                      }}
                    >
                      Actions
                    </TableCell>
                  )}
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredSalaryStructures.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} align="center" sx={{ py: 6 }}>
                      <MoneyIcon
                        sx={{ fontSize: 48, color: "#d1d5db", mb: 1 }}
                      />
                      <Typography sx={{ color: "#6b7280" }}>
                        No salary structures found.
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredSalaryStructures.map((structure, index) => (
                    <TableRow
                      key={structure.id}
                      component={motion.tr}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: index * 0.05 }}
                      sx={{
                        "&:hover": {
                          backgroundColor:
                            theme.palette.mode === "dark"
                              ? "rgba(30,41,59,0.5)"
                              : "#f9fafb",
                        },
                      }}
                    >
                      {/* ID Column */}
                      <TableCell>
                        <Stack spacing={0.5}>
                          <Typography
                            sx={{
                              fontFamily: "monospace",
                              fontWeight: 700,
                              fontSize: "0.875rem",
                              color: "primary.main",
                            }}
                          >
                            #{structure.id}
                          </Typography>

                          <Typography
                            sx={{
                              fontSize: "0.65rem",
                              color:
                                theme.palette.mode === "dark"
                                  ? "#98a3b8"
                                  : "#4c5462",
                            }}
                          >
                            Employee: {structure.employee_id}
                          </Typography>
                          <Typography
                            sx={{
                              fontSize: "0.7rem",
                              color: "text.secondary",
                            }}
                          >
                            Tenant: {structure.tenant_id}
                          </Typography>
                        </Stack>
                      </TableCell>

                      {/* Employee Details Column */}
                      <TableCell>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1.5,
                          }}
                        >
                          <Box
                            sx={{
                              width: 40,
                              height: 40,
                              borderRadius: "50%",
                              flexShrink: 0,
                              background:
                                theme.palette.mode === "dark"
                                  ? "linear-gradient(135deg, rgba(245,158,11,0.2) 0%, rgba(217,119,6,0.2) 100%)"
                                  : "linear-gradient(135deg, #fef3c7 0%, #fef08a 100%)",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                            }}
                          >
                            <PersonIcon
                              sx={{ color: "primary.main", fontSize: 20 }}
                            />
                          </Box>
                          <Box>
                            <Typography
                              sx={{
                                fontWeight: 600,
                                fontSize: "0.875rem",
                                color: "text.primary",
                              }}
                            >
                              {structure.first_name} {structure.last_name || ""}
                            </Typography>
                            <Typography
                              sx={{
                                fontSize: "0.75rem",
                                fontWeight: 500,
                                fontFamily: "monospace",
                                color:
                                  theme.palette.mode === "dark"
                                    ? "#f59e0b"
                                    : "#b8790c",
                              }}
                            >
                              {structure.employee_code}
                            </Typography>
                            <Typography
                              sx={{
                                fontSize: "0.7rem",
                                fontStyle: "italic",
                                color:
                                  theme.palette.mode === "dark"
                                    ? "#8793ab"
                                    : "#777c84",
                              }}
                            >
                              {structure.email}
                            </Typography>
                          </Box>
                        </Box>
                      </TableCell>

                      {/* Basic Salary Column */}
                      <TableCell>
                        <Typography
                          align="center"
                          sx={{
                            fontWeight: 600,
                            fontSize: "0.875rem",
                            color: "primary.main",
                          }}
                        >
                          {formatCurrency(structure.basic)}
                        </Typography>
                      </TableCell>

                      {/* Gross Salary Column */}
                      <TableCell>
                        <Typography
                          align="center"
                          sx={{
                            fontWeight: 600,
                            fontSize: "0.875rem",
                          }}
                        >
                          {formatCurrency(structure.gross_salary)}
                        </Typography>
                      </TableCell>

                      {/* Total Deduction Column */}
                      <TableCell>
                        <Typography
                          align="center"
                          sx={{
                            fontWeight: 600,
                            fontSize: "0.875rem",
                            color: "#bf2626e4",
                          }}
                        >
                          {formatCurrency(structure.total_deduction)}
                        </Typography>
                      </TableCell>

                      {/* Net Salary Column */}
                      <TableCell>
                        <Typography
                          sx={{
                            fontWeight: 700,
                            fontSize: "0.875rem",
                            color: "#10b981",
                          }}
                        >
                          {formatCurrency(structure.net_salary)}
                        </Typography>
                      </TableCell>

                      {/* Status Column */}
                      <TableCell>
                        <StatusBadge status={structure.status} />
                      </TableCell>

                      {/* Actions Column */}
                      {hasAnyPermission([
                        "salary_structure.view",
                        "salary_structure.update",
                        "salary_structure.delete",
                      ]) && (
                        <TableCell align="center">
                          <Stack
                            direction="row"
                            spacing={0.5}
                            justifyContent="center"
                          >
                            <Tooltip title="View Details">
                              <IconButton
                                onClick={() => handleViewClick(structure)}
                                size="small"
                                sx={{
                                  color: "#3b82f6",
                                  "&:hover": {
                                    backgroundColor:
                                      theme.palette.mode === "dark"
                                        ? "rgba(59,130,246,0.2)"
                                        : "#eff6ff",
                                  },
                                }}
                              >
                                <ViewIcon />
                              </IconButton>
                            </Tooltip>
                            {hasPermission("salary_structure.update") && (
                              <Tooltip title="Edit">
                                <IconButton
                                  onClick={() => {
                                    setSelectedSalaryStructure(structure);
                                    setShowEditModal(true);
                                  }}
                                  size="small"
                                  sx={{
                                    color: "primary.main",
                                    "&:hover": {
                                      backgroundColor:
                                        theme.palette.mode === "dark"
                                          ? "rgba(245,158,11,0.2)"
                                          : "#fffbeb",
                                    },
                                  }}
                                >
                                  <EditIcon />
                                </IconButton>
                              </Tooltip>
                            )}
                            {hasPermission("salary_structure.delete") && (
                              <Tooltip title="Inactivate">
                                <IconButton
                                  onClick={() => handleDeleteClick(structure)}
                                  size="small"
                                  sx={{
                                    color: "#ef4444",
                                    "&:hover": {
                                      backgroundColor:
                                        theme.palette.mode === "dark"
                                          ? "rgba(239,68,68,0.2)"
                                          : "#fef2f2",
                                    },
                                  }}
                                >
                                  <DeleteIcon />
                                </IconButton>
                              </Tooltip>
                            )}
                          </Stack>
                        </TableCell>
                      )}
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableWithTopScroll>
        </Paper>
      )}

      {/* Count Summary */}
      {!loading && salaryStructures.length > 0 && (
        <Stack
          direction="row"
          justifyContent="space-between"
          sx={{
            mt: 2,
            gap: 2,
            fontSize: "0.875rem",
            color: "text.secondary",
          }}
        >
          <Typography variant="body2">
            Total Structures: {salaryStructures.length}
          </Typography>
          <Typography variant="body2">
            Showing: {filteredSalaryStructures.length} of{" "}
            {salaryStructures.length}
          </Typography>
        </Stack>
      )}

      {/* View Details Modal */}
      <Dialog
        open={showViewModal}
        onClose={() => setShowViewModal(false)}
        maxWidth="lg"
        fullWidth
        TransitionComponent={motion.div}
        TransitionProps={{
          timeout: 300,
        }}
        PaperProps={{
          sx: {
            borderRadius: "20px",
            overflow: "hidden",
            maxWidth: "900px",
            margin: "16px",
            background:
              theme.palette.mode === "dark"
                ? "linear-gradient(135deg, #0f172a 0%, #1e293b 100%)"
                : "#ffffff",
            boxShadow: theme.shadows[24],
            position: "relative",
          },
        }}
        slotProps={{
          backdrop: {
            sx: {
              backgroundColor: "rgba(0, 0, 0, 0.6)",
              backdropFilter: "blur(4px)",
            },
          },
        }}
      >
        {viewingStructure && (
          <motion.div
            initial={{ y: 50, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 50, opacity: 0 }}
            transition={{ type: "spring", damping: 25, stiffness: 200 }}
          >
            {/* Header with Gradient */}
            <Box
              sx={{
                background: "linear-gradient(135deg, #f59e0b 0%, #d97706 100%)",
                p: 3,
                pr: 5,
                position: "relative",
              }}
            >
              <Stack direction="row" spacing={2} alignItems="center">
                <Box
                  component="img"
                  src={viewDetailsSvg}
                  alt="Details"
                  sx={{
                    width: 50,
                    height: 50,
                    objectFit: "contain",
                    filter: "drop-shadow(0 2px 4px rgba(0,0,0,0.2))",
                  }}
                />
                <Box>
                  <Typography
                    variant="h5"
                    sx={{
                      fontWeight: 800,
                      color: "#ffffff",
                      letterSpacing: "0.5px",
                    }}
                  >
                    REMUNERATION STRUCTURE DETAILS
                  </Typography>
                  <Typography
                    sx={{
                      color: "rgba(255,255,255,0.85)",
                      fontSize: "0.85rem",
                    }}
                  >
                    Employee: {viewingStructure.first_name}{" "}
                    {viewingStructure.last_name || ""} (
                    {viewingStructure.employee_code})
                  </Typography>
                </Box>
              </Stack>

              <IconButton
                onClick={() => setShowViewModal(false)}
                sx={{
                  position: "absolute",
                  top: 16,
                  right: 16,
                  width: 36,
                  height: 36,
                  borderRadius: "10px",
                  color: "#ffffff",
                  backgroundColor: "rgba(255,255,255,0.15)",
                  "&:hover": {
                    backgroundColor: "rgba(255,255,255,0.3)",
                  },
                }}
              >
                <CloseIcon sx={{ fontSize: 20 }} />
              </IconButton>
            </Box>

            {/* Content */}
            <Box
              sx={{
                maxHeight: "calc(80vh - 180px)",
                overflowY: "auto",
                overflowX: "hidden",
                p: 3,
                "&::-webkit-scrollbar": {
                  width: "6px",
                },
                "&::-webkit-scrollbar-track": {
                  background:
                    theme.palette.mode === "dark" ? "#1e293b" : "#f1f5f9",
                  borderRadius: "10px",
                },
                "&::-webkit-scrollbar-thumb": {
                  background: "#f59e0b",
                  borderRadius: "10px",
                },
              }}
            >
              <Stack spacing={3}>
                {/* Section 1: EMPLOYEE INFORMATION */}
                <Box>
                  <Typography
                    sx={{
                      fontSize: "0.75rem",
                      fontWeight: 700,
                      color: "primary.main",
                      letterSpacing: "1.5px",
                      textTransform: "uppercase",
                      mb: 2,
                      borderBottom: `2px solid ${
                        theme.palette.mode === "dark" ? "#334155" : "#f3f4f6"
                      }`,
                      pb: 1,
                    }}
                  >
                    EMPLOYEE INFORMATION
                  </Typography>
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: "1fr 1fr",
                      gap: "8px 24px",
                      p: 2,
                      borderRadius: "12px",
                      background:
                        theme.palette.mode === "dark"
                          ? "rgba(30,41,59,0.4)"
                          : "#f8fafc",
                      border: `1px solid ${
                        theme.palette.mode === "dark" ? "#334155" : "#e2e8f0"
                      }`,
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        py: 1,
                        px: 1.5,
                        borderRadius: "6px",
                        backgroundColor:
                          theme.palette.mode === "dark"
                            ? "rgba(255,255,255,0.03)"
                            : "rgba(0,0,0,0.02)",
                      }}
                    >
                      <Typography
                        sx={{
                          color: "#9ca3af",
                          fontSize: "0.8rem",
                          fontWeight: 500,
                        }}
                      >
                        Employee ID
                      </Typography>
                      <Typography
                        sx={{
                          fontWeight: 600,
                          fontSize: "0.85rem",
                          fontFamily: "monospace",
                          color: "primary.main",
                        }}
                      >
                        #{viewingStructure.id}
                      </Typography>
                    </Box>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        py: 1,
                        px: 1.5,
                        borderRadius: "6px",
                        backgroundColor:
                          theme.palette.mode === "dark"
                            ? "rgba(255,255,255,0.03)"
                            : "rgba(0,0,0,0.02)",
                      }}
                    >
                      <Typography
                        sx={{
                          color: "#9ca3af",
                          fontSize: "0.8rem",
                          fontWeight: 500,
                        }}
                      >
                        Employee Code
                      </Typography>
                      <Typography
                        sx={{
                          fontWeight: 600,
                          fontSize: "0.85rem",
                          fontFamily: "monospace",
                        }}
                      >
                        {viewingStructure.employee_code}
                      </Typography>
                    </Box>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        py: 1,
                        px: 1.5,
                        borderRadius: "6px",
                        backgroundColor:
                          theme.palette.mode === "dark"
                            ? "rgba(255,255,255,0.03)"
                            : "rgba(0,0,0,0.02)",
                      }}
                    >
                      <Typography
                        sx={{
                          color: "#9ca3af",
                          fontSize: "0.8rem",
                          fontWeight: 500,
                        }}
                      >
                        Tenant ID
                      </Typography>
                      <Typography sx={{ fontSize: "0.85rem" }}>
                        {viewingStructure.tenant_id}
                      </Typography>
                    </Box>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        py: 1,
                        px: 1.5,
                        borderRadius: "6px",
                        backgroundColor:
                          theme.palette.mode === "dark"
                            ? "rgba(255,255,255,0.03)"
                            : "rgba(0,0,0,0.02)",
                      }}
                    >
                      <Typography
                        sx={{
                          color: "#9ca3af",
                          fontSize: "0.8rem",
                          fontWeight: 500,
                        }}
                      >
                        Status
                      </Typography>
                      <StatusBadge status={viewingStructure.status} />
                    </Box>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        py: 1,
                        px: 1.5,
                        borderRadius: "6px",
                        backgroundColor:
                          theme.palette.mode === "dark"
                            ? "rgba(255,255,255,0.03)"
                            : "rgba(0,0,0,0.02)",
                      }}
                    >
                      <Typography
                        sx={{
                          color: "#9ca3af",
                          fontSize: "0.8rem",
                          fontWeight: 500,
                        }}
                      >
                        Email
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: "0.85rem",
                          color:
                            theme.palette.mode === "dark"
                              ? "#e5e7eb"
                              : "#1f2937",
                        }}
                      >
                        {viewingStructure.email}
                      </Typography>
                    </Box>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        py: 1,
                        px: 1.5,
                        borderRadius: "6px",
                        backgroundColor:
                          theme.palette.mode === "dark"
                            ? "rgba(255,255,255,0.03)"
                            : "rgba(0,0,0,0.02)",
                      }}
                    >
                      <Typography
                        sx={{
                          color: "#9ca3af",
                          fontSize: "0.8rem",
                          fontWeight: 500,
                        }}
                      >
                        Phone
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: "0.85rem",
                          color:
                            theme.palette.mode === "dark"
                              ? "#e5e7eb"
                              : "#1f2937",
                        }}
                      >
                        {viewingStructure.phone || "-"}
                      </Typography>
                    </Box>
                  </Box>
                </Box>

                {/* Section 2: SALARY BREAKDOWN */}
                <Box>
                  <Typography
                    sx={{
                      fontSize: "0.75rem",
                      fontWeight: 700,
                      color: "primary.main",
                      letterSpacing: "1.5px",
                      textTransform: "uppercase",
                      mb: 2,
                      borderBottom: `2px solid ${
                        theme.palette.mode === "dark" ? "#334155" : "#f3f4f6"
                      }`,
                      pb: 1,
                    }}
                  >
                    SALARY BREAKDOWN
                  </Typography>
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: "1fr 1fr 1fr",
                      gap: "8px 16px",
                      p: 2,
                      borderRadius: "12px",
                      background:
                        theme.palette.mode === "dark"
                          ? "rgba(30,41,59,0.4)"
                          : "#f8fafc",
                      border: `1px solid ${
                        theme.palette.mode === "dark" ? "#334155" : "#e2e8f0"
                      }`,
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        py: 1,
                        px: 1.5,
                        borderRadius: "6px",
                        backgroundColor:
                          theme.palette.mode === "dark"
                            ? "rgba(255,255,255,0.03)"
                            : "rgba(0,0,0,0.02)",
                      }}
                    >
                      <Typography
                        sx={{
                          color: "#9ca3af",
                          fontSize: "0.75rem",
                          fontWeight: 500,
                        }}
                      >
                        Basic Remuneration
                      </Typography>
                      <Typography
                        sx={{
                          fontWeight: 700,
                          fontSize: "0.85rem",
                          color: "primary.main",
                        }}
                      >
                        {formatCurrency(viewingStructure.basic)}
                      </Typography>
                    </Box>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        py: 1,
                        px: 1.5,
                        borderRadius: "6px",
                        backgroundColor:
                          theme.palette.mode === "dark"
                            ? "rgba(255,255,255,0.03)"
                            : "rgba(0,0,0,0.02)",
                      }}
                    >
                      <Typography
                        sx={{
                          color: "#9ca3af",
                          fontSize: "0.75rem",
                          fontWeight: 500,
                        }}
                      >
                        HR Allowance
                      </Typography>
                      <Typography sx={{ fontSize: "0.85rem" }}>
                        {formatCurrency(viewingStructure.hr_allowance)}
                      </Typography>
                    </Box>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        py: 1,
                        px: 1.5,
                        borderRadius: "6px",
                        backgroundColor:
                          theme.palette.mode === "dark"
                            ? "rgba(255,255,255,0.03)"
                            : "rgba(0,0,0,0.02)",
                      }}
                    >
                      <Typography
                        sx={{
                          color: "#9ca3af",
                          fontSize: "0.75rem",
                          fontWeight: 500,
                        }}
                      >
                        Special Allowance
                      </Typography>
                      <Typography sx={{ fontSize: "0.85rem" }}>
                        {formatCurrency(viewingStructure.special_allowance)}
                      </Typography>
                    </Box>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        py: 1,
                        px: 1.5,
                        borderRadius: "6px",
                        backgroundColor:
                          theme.palette.mode === "dark"
                            ? "rgba(255,255,255,0.03)"
                            : "rgba(0,0,0,0.02)",
                      }}
                    >
                      <Typography
                        sx={{
                          color: "#9ca3af",
                          fontSize: "0.75rem",
                          fontWeight: 500,
                        }}
                      >
                        DA
                      </Typography>
                      <Typography sx={{ fontSize: "0.85rem" }}>
                        {formatCurrency(viewingStructure.da)}
                      </Typography>
                    </Box>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        py: 1,
                        px: 1.5,
                        borderRadius: "6px",
                        backgroundColor:
                          theme.palette.mode === "dark"
                            ? "rgba(255,255,255,0.03)"
                            : "rgba(0,0,0,0.02)",
                      }}
                    >
                      <Typography
                        sx={{
                          color: "#9ca3af",
                          fontSize: "0.75rem",
                          fontWeight: 500,
                        }}
                      >
                        Conveyance
                      </Typography>
                      <Typography sx={{ fontSize: "0.85rem" }}>
                        {formatCurrency(viewingStructure.conveyance_allowance)}
                      </Typography>
                    </Box>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        py: 1,
                        px: 1.5,
                        borderRadius: "6px",
                        backgroundColor:
                          theme.palette.mode === "dark"
                            ? "rgba(255,255,255,0.03)"
                            : "rgba(0,0,0,0.02)",
                      }}
                    >
                      <Typography
                        sx={{
                          color: "#9ca3af",
                          fontSize: "0.75rem",
                          fontWeight: 500,
                        }}
                      >
                        Mobile Allowance
                      </Typography>
                      <Typography sx={{ fontSize: "0.85rem" }}>
                        {formatCurrency(viewingStructure.mobile_allowance)}
                      </Typography>
                    </Box>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        py: 1,
                        px: 1.5,
                        borderRadius: "6px",
                        backgroundColor:
                          theme.palette.mode === "dark"
                            ? "rgba(255,255,255,0.03)"
                            : "rgba(0,0,0,0.02)",
                      }}
                    >
                      <Typography
                        sx={{
                          color: "#9ca3af",
                          fontSize: "0.75rem",
                          fontWeight: 500,
                        }}
                      >
                        Other Allowance
                      </Typography>
                      <Typography sx={{ fontSize: "0.85rem" }}>
                        {formatCurrency(viewingStructure.other_allowance)}
                      </Typography>
                    </Box>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        py: 1,
                        px: 1.5,
                        borderRadius: "6px",
                        backgroundColor:
                          theme.palette.mode === "dark"
                            ? "rgba(255,255,255,0.03)"
                            : "rgba(0,0,0,0.02)",
                      }}
                    >
                      <Typography
                        sx={{
                          color: "#9ca3af",
                          fontSize: "0.75rem",
                          fontWeight: 500,
                        }}
                      >
                        Incentive
                      </Typography>
                      <Typography sx={{ fontSize: "0.85rem" }}>
                        {formatCurrency(viewingStructure.incentive)}
                      </Typography>
                    </Box>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        py: 1,
                        px: 1.5,
                        borderRadius: "6px",
                        backgroundColor:
                          theme.palette.mode === "dark"
                            ? "rgba(255,255,255,0.03)"
                            : "rgba(0,0,0,0.02)",
                      }}
                    >
                      <Typography
                        sx={{
                          color: "#9ca3af",
                          fontSize: "0.75rem",
                          fontWeight: 500,
                        }}
                      >
                        Travel Allowance
                      </Typography>
                      <Typography sx={{ fontSize: "0.85rem" }}>
                        {formatCurrency(viewingStructure.travel_allowance)}
                      </Typography>
                    </Box>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        py: 1,
                        px: 1.5,
                        borderRadius: "6px",
                        backgroundColor:
                          theme.palette.mode === "dark"
                            ? "rgba(255,255,255,0.03)"
                            : "rgba(0,0,0,0.02)",
                      }}
                    >
                      <Typography
                        sx={{
                          color: "#9ca3af",
                          fontSize: "0.75rem",
                          fontWeight: 500,
                        }}
                      >
                        Bonus
                      </Typography>
                      <Typography sx={{ fontSize: "0.85rem" }}>
                        {formatCurrency(viewingStructure.bonus)}
                      </Typography>
                    </Box>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        py: 1,
                        px: 1.5,
                        borderRadius: "6px",
                        backgroundColor:
                          theme.palette.mode === "dark"
                            ? "rgba(255,255,255,0.03)"
                            : "rgba(0,0,0,0.02)",
                      }}
                    >
                      <Typography
                        sx={{
                          color: "#9ca3af",
                          fontSize: "0.75rem",
                          fontWeight: 500,
                        }}
                      >
                        Mediclaim
                      </Typography>
                      <Typography sx={{ fontSize: "0.85rem" }}>
                        {formatCurrency(viewingStructure.mediclaim)}
                      </Typography>
                    </Box>
                  </Box>
                </Box>

                {/* Section 3: SALARY SUMMARY */}
                <Box>
                  <Typography
                    sx={{
                      fontSize: "0.75rem",
                      fontWeight: 700,
                      color: "primary.main",
                      letterSpacing: "1.5px",
                      textTransform: "uppercase",
                      mb: 2,
                      borderBottom: `2px solid ${
                        theme.palette.mode === "dark" ? "#334155" : "#f3f4f6"
                      }`,
                      pb: 1,
                    }}
                  >
                    SALARY SUMMARY
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={6} md={3}>
                      <Box
                        sx={{
                          p: 2,
                          borderRadius: "12px",
                          textAlign: "center",
                          background:
                            theme.palette.mode === "dark"
                              ? "rgba(59,130,246,0.15)"
                              : "#eff6ff",
                          border: `1px solid ${
                            theme.palette.mode === "dark"
                              ? "rgba(59,130,246,0.3)"
                              : "#bfdbfe"
                          }`,
                        }}
                      >
                        <Typography
                          sx={{
                            color: "#3b82f6",
                            fontSize: "0.7rem",
                            fontWeight: 700,
                            textTransform: "uppercase",
                            letterSpacing: "0.5px",
                          }}
                        >
                          Gross Remuneration
                        </Typography>
                        <Typography
                          sx={{
                            fontWeight: 800,
                            fontSize: "1.1rem",
                            color: "#3b82f6",
                          }}
                        >
                          {formatCurrency(viewingStructure.gross_salary)}
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={6} md={3}>
                      <Box
                        sx={{
                          p: 2,
                          borderRadius: "12px",
                          textAlign: "center",
                          background:
                            theme.palette.mode === "dark"
                              ? "rgba(139,92,246,0.15)"
                              : "#ede9fe",
                          border: `1px solid ${
                            theme.palette.mode === "dark"
                              ? "rgba(139,92,246,0.3)"
                              : "#c4b5fd"
                          }`,
                        }}
                      >
                        <Typography
                          sx={{
                            color: "#8b5cf6",
                            fontSize: "0.7rem",
                            fontWeight: 700,
                            textTransform: "uppercase",
                            letterSpacing: "0.5px",
                          }}
                        >
                          CTC Component
                        </Typography>
                        <Typography
                          sx={{
                            fontWeight: 800,
                            fontSize: "1.1rem",
                            color: "#8b5cf6",
                          }}
                        >
                          {formatCurrency(viewingStructure.total_ctc_component)}
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={6} md={3}>
                      <Box
                        sx={{
                          p: 2,
                          borderRadius: "12px",
                          textAlign: "center",
                          background:
                            theme.palette.mode === "dark"
                              ? "rgba(239,68,68,0.15)"
                              : "#fee2e2",
                          border: `1px solid ${
                            theme.palette.mode === "dark"
                              ? "rgba(239,68,68,0.3)"
                              : "#fca5a5"
                          }`,
                        }}
                      >
                        <Typography
                          sx={{
                            color: "#ef4444",
                            fontSize: "0.7rem",
                            fontWeight: 700,
                            textTransform: "uppercase",
                            letterSpacing: "0.5px",
                          }}
                        >
                          Total Deduction
                        </Typography>
                        <Typography
                          sx={{
                            fontWeight: 800,
                            fontSize: "1.1rem",
                            color: "#ef4444",
                          }}
                        >
                          {formatCurrency(viewingStructure.total_deduction)}
                        </Typography>
                      </Box>
                    </Grid>

                    <Grid item xs={6} md={3}>
                      <Box
                        sx={{
                          p: 2,
                          borderRadius: "12px",
                          textAlign: "center",
                          background:
                            theme.palette.mode === "dark"
                              ? "rgba(16,185,129,0.15)"
                              : "#d1fae5",
                          border: `1px solid ${
                            theme.palette.mode === "dark"
                              ? "rgba(16,185,129,0.3)"
                              : "#6ee7b7"
                          }`,
                        }}
                      >
                        <Typography
                          sx={{
                            color: "#10b981",
                            fontSize: "0.7rem",
                            fontWeight: 700,
                            textTransform: "uppercase",
                            letterSpacing: "0.5px",
                          }}
                        >
                          Net Remuneration
                        </Typography>
                        <Typography
                          sx={{
                            fontWeight: 800,
                            fontSize: "1.1rem",
                            color: "#10b981",
                          }}
                        >
                          {formatCurrency(viewingStructure.net_salary)}
                        </Typography>
                      </Box>
                    </Grid>
                  </Grid>
                </Box>

                {/* Section 4: DEDUCTION SETTINGS */}
                <Box>
                  <Typography
                    sx={{
                      fontSize: "0.75rem",
                      fontWeight: 700,
                      color: "primary.main",
                      letterSpacing: "1.5px",
                      textTransform: "uppercase",
                      mb: 2,
                      borderBottom: `2px solid ${
                        theme.palette.mode === "dark" ? "#334155" : "#f3f4f6"
                      }`,
                      pb: 1,
                    }}
                  >
                    DEDUCTION SETTINGS
                  </Typography>
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: "1fr 1fr 1fr",
                      gap: "8px 16px",
                      p: 2,
                      borderRadius: "12px",
                      background:
                        theme.palette.mode === "dark"
                          ? "rgba(30,41,59,0.4)"
                          : "#f8fafc",
                      border: `1px solid ${
                        theme.palette.mode === "dark" ? "#334155" : "#e2e8f0"
                      }`,
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        py: 1,
                        px: 1.5,
                        borderRadius: "6px",
                        backgroundColor:
                          theme.palette.mode === "dark"
                            ? "rgba(255,255,255,0.03)"
                            : "rgba(0,0,0,0.02)",
                      }}
                    >
                      <Typography
                        sx={{
                          color: "#9ca3af",
                          fontSize: "0.75rem",
                          fontWeight: 500,
                        }}
                      >
                        TDS %
                      </Typography>
                      <Typography sx={{ fontWeight: 600, fontSize: "0.85rem" }}>
                        {viewingStructure.tds_percentage}%{" "}
                        {viewingStructure.tds_type === "number"
                          ? "(Derived)"
                          : ""}
                      </Typography>
                    </Box>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        py: 1,
                        px: 1.5,
                        borderRadius: "6px",
                        backgroundColor:
                          theme.palette.mode === "dark"
                            ? "rgba(255,255,255,0.03)"
                            : "rgba(0,0,0,0.02)",
                      }}
                    >
                      <Typography
                        sx={{
                          color: "#9ca3af",
                          fontSize: "0.75rem",
                          fontWeight: 500,
                        }}
                      >
                        TDS Amount
                      </Typography>
                      <Typography
                        sx={{
                          fontWeight: 600,
                          fontSize: "0.85rem",
                          color: "#ef4444",
                        }}
                      >
                        {formatCurrency(viewingStructure.tds)}{" "}
                        {viewingStructure.tds_type === "percentage"
                          ? "(Derived)"
                          : ""}
                      </Typography>
                    </Box>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        py: 1,
                        px: 1.5,
                        borderRadius: "6px",
                        backgroundColor:
                          theme.palette.mode === "dark"
                            ? "rgba(255,255,255,0.03)"
                            : "rgba(0,0,0,0.02)",
                      }}
                    >
                      <Typography
                        sx={{
                          color: "#9ca3af",
                          fontSize: "0.75rem",
                          fontWeight: 500,
                        }}
                      >
                        PF
                      </Typography>
                      <Chip
                        label={
                          viewingStructure.pf_applicable === 1
                            ? `YES (${
                                viewingStructure.pf_type === "number"
                                  ? "₹" + viewingStructure.pf_amount
                                  : viewingStructure.pf_percentage + "%"
                              })`
                            : "NO"
                        }
                        size="small"
                        sx={{
                          backgroundColor:
                            viewingStructure.pf_applicable === 1
                              ? "#d1fae5"
                              : "#fee2e2",
                          color:
                            viewingStructure.pf_applicable === 1
                              ? "#065f46"
                              : "#991b1b",
                          fontWeight: 700,
                          fontSize: "0.7rem",
                          height: "24px",
                          borderRadius: "6px",
                        }}
                      />
                    </Box>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        py: 1,
                        px: 1.5,
                        borderRadius: "6px",
                        backgroundColor:
                          theme.palette.mode === "dark"
                            ? "rgba(255,255,255,0.03)"
                            : "rgba(0,0,0,0.02)",
                      }}
                    >
                      <Typography
                        sx={{
                          color: "#9ca3af",
                          fontSize: "0.75rem",
                          fontWeight: 500,
                        }}
                      >
                        ESI
                      </Typography>
                      <Chip
                        label={
                          viewingStructure.esi_applicable === 1
                            ? `YES (${
                                viewingStructure.esi_type === "number"
                                  ? "₹" + viewingStructure.esi_amount
                                  : viewingStructure.esi_percentage + "%"
                              })`
                            : "NO"
                        }
                        size="small"
                        sx={{
                          backgroundColor:
                            viewingStructure.esi_applicable === 1
                              ? "#d1fae5"
                              : "#fee2e2",
                          color:
                            viewingStructure.esi_applicable === 1
                              ? "#065f46"
                              : "#991b1b",
                          fontWeight: 700,
                          fontSize: "0.7rem",
                          height: "24px",
                          borderRadius: "6px",
                        }}
                      />
                    </Box>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        py: 1,
                        px: 1.5,
                        borderRadius: "6px",
                        backgroundColor:
                          theme.palette.mode === "dark"
                            ? "rgba(255,255,255,0.03)"
                            : "rgba(0,0,0,0.02)",
                      }}
                    >
                      <Typography
                        sx={{
                          color: "#9ca3af",
                          fontSize: "0.75rem",
                          fontWeight: 500,
                        }}
                      >
                        PT
                      </Typography>
                      <Chip
                        label={
                          viewingStructure.pt_applicable === 1
                            ? `YES (${
                                viewingStructure.pt_type === "number"
                                  ? "₹" + viewingStructure.pt_amount
                                  : viewingStructure.pt_percentage + "%"
                              })`
                            : "NO"
                        }
                        size="small"
                        sx={{
                          backgroundColor:
                            viewingStructure.pt_applicable === 1
                              ? "#d1fae5"
                              : "#fee2e2",
                          color:
                            viewingStructure.pt_applicable === 1
                              ? "#065f46"
                              : "#991b1b",
                          fontWeight: 700,
                          fontSize: "0.7rem",
                          height: "24px",
                          borderRadius: "6px",
                        }}
                      />
                    </Box>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        py: 1,
                        px: 1.5,
                        borderRadius: "6px",
                        backgroundColor:
                          theme.palette.mode === "dark"
                            ? "rgba(255,255,255,0.03)"
                            : "rgba(0,0,0,0.02)",
                      }}
                    >
                      <Typography
                        sx={{
                          color: "#9ca3af",
                          fontSize: "0.75rem",
                          fontWeight: 500,
                        }}
                      >
                        Attendance Based Salary
                      </Typography>
                      <Chip
                        label={
                          viewingStructure.attendance_based_salary === 1
                            ? "YES"
                            : "NO"
                        }
                        size="small"
                        sx={{
                          backgroundColor:
                            viewingStructure.attendance_based_salary === 1
                              ? "#d1fae5"
                              : "#fee2e2",
                          color:
                            viewingStructure.attendance_based_salary === 1
                              ? "#065f46"
                              : "#991b1b",
                          fontWeight: 700,
                          fontSize: "0.7rem",
                          height: "24px",
                          borderRadius: "6px",
                        }}
                      />
                    </Box>
                  </Box>
                </Box>

                {/* Section 5: TIMESTAMPS */}
                <Box>
                  <Typography
                    sx={{
                      fontSize: "0.75rem",
                      fontWeight: 700,
                      color: "primary.main",
                      letterSpacing: "1.5px",
                      textTransform: "uppercase",
                      mb: 2,
                      borderBottom: `2px solid ${
                        theme.palette.mode === "dark" ? "#334155" : "#f3f4f6"
                      }`,
                      pb: 1,
                    }}
                  >
                    TIMESTAMPS
                  </Typography>
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: "1fr 1fr",
                      gap: "8px 24px",
                      p: 2,
                      borderRadius: "12px",
                      background:
                        theme.palette.mode === "dark"
                          ? "rgba(30,41,59,0.4)"
                          : "#f8fafc",
                      border: `1px solid ${
                        theme.palette.mode === "dark" ? "#334155" : "#e2e8f0"
                      }`,
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        py: 1,
                        px: 1.5,
                        borderRadius: "6px",
                        backgroundColor:
                          theme.palette.mode === "dark"
                            ? "rgba(255,255,255,0.03)"
                            : "rgba(0,0,0,0.02)",
                      }}
                    >
                      <Typography
                        sx={{
                          color: "#9ca3af",
                          fontSize: "0.75rem",
                          fontWeight: 500,
                        }}
                      >
                        Created At
                      </Typography>
                      <Typography sx={{ fontSize: "0.85rem" }}>
                        {formatDateTime(viewingStructure.created_at)}
                      </Typography>
                    </Box>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        py: 1,
                        px: 1.5,
                        borderRadius: "6px",
                        backgroundColor:
                          theme.palette.mode === "dark"
                            ? "rgba(255,255,255,0.03)"
                            : "rgba(0,0,0,0.02)",
                      }}
                    >
                      <Typography
                        sx={{
                          color: "#9ca3af",
                          fontSize: "0.75rem",
                          fontWeight: 500,
                        }}
                      >
                        Updated At
                      </Typography>
                      <Typography sx={{ fontSize: "0.85rem" }}>
                        {formatDateTime(viewingStructure.updated_at)}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              </Stack>
            </Box>

            {/* Footer */}
            <Box
              sx={{
                p: 2.5,
                borderTop: `1px solid ${
                  theme.palette.mode === "dark" ? "#1e293b" : "#f3f4f6"
                }`,
                display: "flex",
                justifyContent: "flex-end",
                gap: 2,
              }}
            >
              <Button
                onClick={() => {
                  setSelectedSalaryStructure(viewingStructure);
                  setShowViewModal(false);
                  setShowEditModal(true);
                }}
                variant="outlined"
                startIcon={<EditIcon />}
                sx={{
                  borderRadius: "10px",
                  px: 3,
                  textTransform: "none",
                  fontWeight: 600,
                  fontSize: "0.8rem",
                  borderColor: "#f59e0b",
                  color: "primary.main",
                  "&:hover": {
                    borderColor: "#d97706",
                    backgroundColor: "rgba(245,158,11,0.08)",
                  },
                }}
              >
                Edit Structure
              </Button>
              <Button
                onClick={() => setShowViewModal(false)}
                variant="contained"
                sx={{
                  borderRadius: "10px",
                  px: 3,
                  textTransform: "none",
                  fontWeight: 600,
                  fontSize: "0.8rem",
                  background:
                    "linear-gradient(135deg, #f59e0b 0%, #d97706 100%)",
                  "&:hover": {
                    background:
                      "linear-gradient(135deg, #d97706 0%, #b45309 100%)",
                  },
                }}
              >
                Close
              </Button>
            </Box>
          </motion.div>
        )}
      </Dialog>

      {/* Modals */}
      <AddSalaryStructureModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onSuccess={() => dispatch(fetchSalaryStructures())}
      />

      <EditSalaryStructureModal
        isOpen={showEditModal}
        onClose={() => {
          setShowEditModal(false);
          setSelectedSalaryStructure(null);
        }}
        salaryStructure={selectedSalaryStructure}
        onSuccess={() => dispatch(fetchSalaryStructures())}
      />

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={handleDeleteCancel}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: "20px",
            overflow: "hidden",
            background:
              theme.palette.mode === "dark"
                ? "linear-gradient(135deg, #0f172a 0%, #1e293b 100%)"
                : "#ffffff",
          },
        }}
      >
        <Box
          sx={{
            background: "linear-gradient(135deg, #f59e0b 0%, #d97706 100%)",
            height: "6px",
          }}
        />
        <Box sx={{ p: 3, position: "relative" }}>
          <IconButton
            onClick={handleDeleteCancel}
            sx={{
              position: "absolute",
              top: 16,
              right: 16,
              width: 32,
              height: 32,
              borderRadius: "8px",
              "&:hover": { backgroundColor: "#f59e0b", color: "#ffffff" },
            }}
          >
            <CloseIcon sx={{ fontSize: 18 }} />
          </IconButton>
          <Typography
            variant="h6"
            sx={{ fontWeight: 700, mb: 2, textAlign: "center" }}
          >
            Delete Remuneration Structure
          </Typography>
          <Typography sx={{ textAlign: "center", mb: 1 }}>
            Are you sure you want to delete
          </Typography>
          <Typography
            sx={{
              fontWeight: 600,
              color: "primary.main",
              textAlign: "center",
              mb: 2,
            }}
          >
            "{salaryStructureToDelete?.first_name}{" "}
            {salaryStructureToDelete?.last_name || ""} -{" "}
            {salaryStructureToDelete?.employee_code}"
          </Typography>
          <Stack direction="row" spacing={2}>
            <Button
              onClick={handleDeleteCancel}
              disabled={deleting}
              fullWidth
              variant="outlined"
              sx={{ borderRadius: "12px" }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleDeleteConfirm}
              disabled={deleting}
              fullWidth
              variant="contained"
              sx={{
                background: "linear-gradient(135deg, #f59e0b 0%, #d97706 100%)",
                borderRadius: "12px",
              }}
            >
              {deleting ? (
                <CircularProgress size={20} sx={{ color: "#ffffff" }} />
              ) : (
                "Delete"
              )}
            </Button>
          </Stack>
        </Box>
      </Dialog>
    </Box>
  );
};

export default SalaryStructure;
